'use client';

import { c as _c } from "react/compiler-runtime";
import { ApiError } from '@lib/api/api-response';
import { parseApiErrorResponse } from '@lib/api/utils/parse-api-error-response';
import { useAuthedMutation } from '@lib/query/use-authed-query';
import type { FlowDocumentProcessingRetryResult, FlowDocumentProcessingRetryVariables } from '../types/flow-document-processing-retry-result';
async function retryFlowDocumentProcessingClient(input: FlowDocumentProcessingRetryVariables): Promise<FlowDocumentProcessingRetryResult> {
  const response = await fetch(`/api/flows/${input.flowId}/collections/${input.collectionId}/retry-processing`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      ...(input.storagePath ? {
        storagePath: input.storagePath
      } : {}),
      ...(input.name ? {
        name: input.name
      } : {})
    })
  });
  if (response.ok) {
    return (await response.json()) as FlowDocumentProcessingRetryResult;
  }
  const {
    reason,
    body
  } = await parseApiErrorResponse(response);
  if (response.status === 409 && body && typeof body === 'object' && 'status' in body && (body as {
    status: unknown;
  }).status === 'needs_reupload') {
    return {
      status: 'needs_reupload'
    };
  }
  if (response.status === 422 && body && typeof body === 'object' && 'status' in body && (body as {
    status: unknown;
  }).status === 'failed') {
    return {
      status: 'failed'
    };
  }
  const message = reason || `Request failed (HTTP ${response.status})`;
  throw new ApiError(message, response.status, body);
}

/** Re-queue backend field detection for a failed (or stuck) collection upload. */
export function useRetryFlowDocumentProcessing() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: retryFlowDocumentProcessingClient
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useAuthedMutation(t0);
}